import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { useCRM } from '../context/CRMContext';
import { useSubscription } from '../context/SubscriptionContext';
import { PricingDiscountsView } from '../components/superadmin/SuperAdminManagementViews';

const SuperAdmin = () => {
  const navigate = useNavigate();
  const {
    users,
    currentUser,
    platformProfiles,
    platformCustomers,
    platformAuditLog,
    superAdminLoading,
    loadSuperAdminData,
    createPlatformCustomer,
    updatePlatformCustomer,
    createEmployeeInvite,
    updateEmployee
  } = useCRM();
  const {
    demoBookings,
    updateDemoBooking,
    pendingPartners,
    partners,
    plans,
    portalResources,
    addPortalResource,
    deletePortalResource,
    discountCodes,
    discountCodesLoading,
    loadDiscountCodes,
    createDiscountCode,
    updateDiscountCode
  } = useSubscription();
  const [activeView, setActiveView] = useState('grid');
  
  // State for ny ressurs
  const [newResource, setNewResource] = useState({ title: '', type: 'Document', audience: 'all', description: '' });
  const [customerForm, setCustomerForm] = useState({
    company_name: '',
    contact_email: '',
    plan: 'Trial',
    status: 'Active',
    seats: 1,
    monthly_value: 0,
    started_at: new Date().toISOString().slice(0, 10),
    notes: ''
  });
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [customerSaving, setCustomerSaving] = useState(false);
  const [customerMessage, setCustomerMessage] = useState('');
  const [employeeForm, setEmployeeForm] = useState({ name: '', email: '', role: 'Support' });
  const [showEmployeeForm, setShowEmployeeForm] = useState(false);
  const [employeeSaving, setEmployeeSaving] = useState(false);
  const [employeeMessage, setEmployeeMessage] = useState('');

  const [countries, setCountries] = useState([
    { id: 'no', name: 'Norge', code: 'NO', departments: ['Salg', 'Support', 'Finance'] },
    { id: 'se', name: 'Sverige', code: 'SE', departments: [] },
    { id: 'dk', name: 'Danmark', code: 'DK', departments: [] }
  ]);
  const [countryForm, setCountryForm] = useState({ name: '', code: '' });
  const [departmentForm, setDepartmentForm] = useState({ countryId: 'no', name: '' });
  const [pricing, setPricing] = useState(() =>
    (plans || []).map(plan => ({ ...plan, discount: 0, active: true }))
  );
  const [partnerList, setPartnerList] = useState(partners || []);
  const [pendingPartnerList, setPendingPartnerList] = useState(pendingPartners || []);

  const activeCustomers = (platformCustomers || []).filter(customer => customer.status === 'Active');
  const monthlyRevenue = (platformCustomers || []).reduce((sum, customer) => sum + Number(customer.monthly_value || 0), 0);
  const internalEmployees = (platformProfiles || []).filter(profile => profile.role === 'SuperAdmin');

  const submitCustomer = async (event) => {
    event.preventDefault();
    setCustomerSaving(true);
    setCustomerMessage('');

    const result = await createPlatformCustomer(customerForm);

    setCustomerSaving(false);

    if (!result.success) {
      setCustomerMessage(result.error?.message || 'Kunne ikke opprette kunden.');
      return;
    }

    setCustomerForm({
      company_name: '',
      contact_email: '',
      plan: 'Trial',
      status: 'Active',
      seats: 1,
      monthly_value: 0,
      started_at: new Date().toISOString().slice(0, 10),
      notes: ''
    });
    setShowCustomerForm(false);
    setCustomerMessage('Kunden er registrert.');
  };

  const submitEmployee = async (event) => {
    event.preventDefault();
    setEmployeeSaving(true);
    setEmployeeMessage('');

    const result = await createEmployeeInvite(employeeForm);

    setEmployeeSaving(false);

    if (!result.success) {
      setEmployeeMessage(result.error?.message || 'Kunne ikke registrere den ansatte.');
      return;
    }

    setEmployeeForm({ name: '', email: '', role: 'Support' });
    setShowEmployeeForm(false);
    setEmployeeMessage('Ansatt er registrert. Den ansatte kan opprette konto med denne e-postadressen.');
  };

  const renderCustomers = () => (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
      <div className="bg-white px-8 py-6 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => setActiveView('grid')} className="text-slate-400 hover:text-slate-900"><SafeIcon icon="⬅️" /></button>
          <div>
            <h1 className="text-xl font-bold">Kunder og abonnementer</h1>
            <p className="text-xs text-slate-500">Plattformens kundekontoer og tilgangsstatus</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={loadSuperAdminData} className="px-4 py-2 rounded-lg bg-slate-900 text-white text-xs font-bold hover:bg-slate-700">
            Oppdater
          </button>
          <button
            onClick={() => setShowCustomerForm(current => !current)}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white text-xs font-bold hover:bg-blue-700"
          >
            {showCustomerForm ? 'Lukk' : '+ Legg til kunde'}
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto mt-8 px-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <p className="text-xs uppercase tracking-widest text-slate-400 font-black">Kunder</p>
            <p className="text-3xl font-black mt-2">{(platformCustomers || []).length}</p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <p className="text-xs uppercase tracking-widest text-slate-400 font-black">Aktive</p>
            <p className="text-3xl font-black mt-2">{activeCustomers.length}</p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <p className="text-xs uppercase tracking-widest text-slate-400 font-black">Plattformbrukere</p>
            <p className="text-3xl font-black mt-2">{(platformProfiles || []).length}</p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <p className="text-xs uppercase tracking-widest text-slate-400 font-black">Månedlig verdi</p>
            <p className="text-3xl font-black mt-2">{monthlyRevenue.toLocaleString('nb-NO')} kr</p>
          </div>
        </div>

        {showCustomerForm && (
          <form onSubmit={submitCustomer} className="bg-white rounded-2xl border border-slate-200 p-6 mb-6 shadow-sm">
            <div className="flex items-start justify-between gap-4 mb-5">
              <div>
                <h2 className="font-bold text-slate-900">Registrer ny kunde</h2>
                <p className="text-xs text-slate-500 mt-1">Dette oppretter kundekontoen i plattformregisteret.</p>
              </div>
              <span className="px-2 py-1 rounded bg-blue-50 text-blue-700 text-[10px] font-black uppercase tracking-widest">
                Manuell
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Firmanavn</label>
                <input
                  required
                  value={customerForm.company_name}
                  onChange={event => setCustomerForm({ ...customerForm, company_name: event.target.value })}
                  placeholder="F.eks. Acme AS"
                  className="w-full p-3 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Kontakt-e-post</label>
                <input
                  required
                  type="email"
                  value={customerForm.contact_email}
                  onChange={event => setCustomerForm({ ...customerForm, contact_email: event.target.value })}
                  placeholder="kontakt@bedrift.no"
                  className="w-full p-3 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Abonnement</label>
                <select
                  value={customerForm.plan}
                  onChange={event => setCustomerForm({ ...customerForm, plan: event.target.value })}
                  className="w-full p-3 border border-slate-200 rounded-xl text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Trial">Trial</option>
                  <option value="Starter">Starter</option>
                  <option value="Professional">Professional</option>
                  <option value="Enterprise">Enterprise</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Status</label>
                <select
                  value={customerForm.status}
                  onChange={event => setCustomerForm({ ...customerForm, status: event.target.value })}
                  className="w-full p-3 border border-slate-200 rounded-xl text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Active">Aktiv</option>
                  <option value="PastDue">Forfalt</option>
                  <option value="Suspended">Sperret</option>
                  <option value="Cancelled">Avsluttet</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Antall seter</label>
                <input
                  required
                  min="1"
                  type="number"
                  value={customerForm.seats}
                  onChange={event => setCustomerForm({ ...customerForm, seats: event.target.value })}
                  className="w-full p-3 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Månedlig verdi (kr)</label>
                <input
                  required
                  min="0"
                  type="number"
                  value={customerForm.monthly_value}
                  onChange={event => setCustomerForm({ ...customerForm, monthly_value: event.target.value })}
                  className="w-full p-3 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Startdato</label>
                <input
                  required
                  type="date"
                  value={customerForm.started_at}
                  onChange={event => setCustomerForm({ ...customerForm, started_at: event.target.value })}
                  className="w-full p-3 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Internt notat</label>
                <input
                  value={customerForm.notes}
                  onChange={event => setCustomerForm({ ...customerForm, notes: event.target.value })}
                  placeholder="Valgfritt"
                  className="w-full p-3 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="mt-5 flex items-center justify-between gap-4">
              <p className="text-xs text-slate-500">
                Kunden får ikke automatisk en innlogget bruker. Bruker kan kobles til senere.
              </p>
              <button
                type="submit"
                disabled={customerSaving}
                className="px-5 py-3 rounded-xl bg-slate-900 text-white text-sm font-bold disabled:opacity-60"
              >
                {customerSaving ? 'Lagrer...' : 'Lagre kunde'}
              </button>
            </div>
          </form>
        )}

        {customerMessage && (
          <p className="mb-4 text-sm font-medium text-blue-700">{customerMessage}</p>
        )}

        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
          {superAdminLoading ? (
            <div className="p-10 text-center text-slate-500">Laster plattformdata...</div>
          ) : (platformCustomers || []).length === 0 ? (
            <div className="p-10 text-center text-slate-500">Ingen kundekontoer er registrert enda.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-4 font-bold text-slate-600">Kunde</th>
                    <th className="px-6 py-4 font-bold text-slate-600">Kontakt</th>
                    <th className="px-6 py-4 font-bold text-slate-600">Plan</th>
                    <th className="px-6 py-4 font-bold text-slate-600">Seter</th>
                    <th className="px-6 py-4 font-bold text-slate-600">Verdi</th>
                    <th className="px-6 py-4 font-bold text-slate-600">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(platformCustomers || []).map(customer => (
                    <tr key={customer.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4">
                        <p className="font-bold text-slate-900">{customer.company_name}</p>
                        <p className="text-xs text-slate-500">Startet {customer.started_at || '—'}</p>
                      </td>
                      <td className="px-6 py-4 text-slate-600">{customer.contact_email || '—'}</td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 rounded bg-blue-50 text-blue-700 text-xs font-bold">{customer.plan}</span>
                      </td>
                      <td className="px-6 py-4">{customer.seats || 0}</td>
                      <td className="px-6 py-4">{Number(customer.monthly_value || 0).toLocaleString('nb-NO')} kr</td>
                      <td className="px-6 py-4">
                        <select
                          value={customer.status || 'Active'}
                          onChange={(e) => updatePlatformCustomer(customer.id, { status: e.target.value })}
                          className="p-2 border border-slate-300 rounded-lg text-xs bg-white"
                        >
                          <option value="Active">Aktiv</option>
                          <option value="PastDue">Forfalt</option>
                          <option value="Suspended">Sperret</option>
                          <option value="Cancelled">Avsluttet</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderEmployees = () => (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
      <div className="bg-white px-8 py-6 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => setActiveView('grid')} className="text-slate-400 hover:text-slate-900"><SafeIcon icon="⬅️" /></button>
          <div>
            <h1 className="text-xl font-bold">Interne ansatte</h1>
            <p className="text-xs text-slate-500">Ansatte med administrativ tilgang til plattformen</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={loadSuperAdminData} className="px-4 py-2 rounded-lg bg-slate-900 text-white text-xs font-bold hover:bg-slate-700">
            Oppdater
          </button>
          <button
            onClick={() => setShowEmployeeForm(current => !current)}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white text-xs font-bold hover:bg-blue-700"
          >
            {showEmployeeForm ? 'Lukk' : '+ Legg til ansatt'}
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto mt-8 px-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <p className="text-xs uppercase tracking-widest text-slate-400 font-black">Interne ansatte</p>
            <p className="text-3xl font-black mt-2">{internalEmployees.length}</p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <p className="text-xs uppercase tracking-widest text-slate-400 font-black">Aktive</p>
            <p className="text-3xl font-black mt-2">{internalEmployees.filter(employee => employee.status === 'Active').length}</p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <p className="text-xs uppercase tracking-widest text-slate-400 font-black">Tilgangsnivå</p>
            <p className="text-3xl font-black mt-2">Admin</p>
          </div>
        </div>

        {showEmployeeForm && (
          <form onSubmit={submitEmployee} className="bg-white rounded-2xl border border-slate-200 p-6 mb-6 shadow-sm">
            <h2 className="font-bold text-slate-900 mb-4">Registrer ny intern ansatt</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <input
                required
                value={employeeForm.name}
                onChange={event => setEmployeeForm({ ...employeeForm, name: event.target.value })}
                placeholder="Fullt navn"
                className="p-3 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                required
                type="email"
                value={employeeForm.email}
                onChange={event => setEmployeeForm({ ...employeeForm, email: event.target.value })}
                placeholder="jobb@bedrift.no"
                className="p-3 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500"
              />
              <select
                value={employeeForm.role}
                onChange={event => setEmployeeForm({ ...employeeForm, role: event.target.value })}
                className="p-3 border border-slate-200 rounded-xl text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="SuperAdmin">SuperAdmin</option>
                <option value="PlatformAdmin">PlatformAdmin</option>
                <option value="Support">Support</option>
                <option value="Sales">Sales</option>
                <option value="Finance">Finance</option>
              </select>
            </div>
            <div className="mt-4 flex items-center justify-between gap-4">
              <p className="text-xs text-slate-500">
                Den ansatte registrerer deretter konto med samme e-postadresse.
              </p>
              <button
                type="submit"
                disabled={employeeSaving}
                className="px-5 py-3 rounded-xl bg-slate-900 text-white text-sm font-bold disabled:opacity-60"
              >
                {employeeSaving ? 'Registrerer...' : 'Registrer ansatt'}
              </button>
            </div>
          </form>
        )}

        {employeeMessage && <p className="mb-4 text-sm font-medium text-blue-700">{employeeMessage}</p>}

        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
          {superAdminLoading ? (
            <div className="p-10 text-center text-slate-500">Laster ansattdata...</div>
          ) : internalEmployees.length === 0 ? (
            <div className="p-10 text-center text-slate-500">Ingen interne ansatte er registrert enda.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-4 font-bold text-slate-600">Navn</th>
                    <th className="px-6 py-4 font-bold text-slate-600">E-post</th>
                    <th className="px-6 py-4 font-bold text-slate-600">Rolle</th>
                    <th className="px-6 py-4 font-bold text-slate-600">Status</th>
                    <th className="px-6 py-4 font-bold text-slate-600">Opprettet</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {internalEmployees.map(employee => (
                    <tr key={employee.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4 font-bold text-slate-900">{employee.name || '—'}</td>
                      <td className="px-6 py-4 text-slate-600">{employee.email || '—'}</td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 rounded bg-violet-50 text-violet-700 text-xs font-bold">
                          {employee.role}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded text-xs font-bold ${employee.status === 'Active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                          {employee.status === 'Active' ? 'Aktiv' : employee.status || '—'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-slate-600">
                        {employee.created_at ? new Date(employee.created_at).toLocaleDateString('nb-NO') : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderBookings = () => (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
      <div className="bg-white px-8 py-6 border-b border-slate-200 flex items-center gap-4">
        <button onClick={() => setActiveView('grid')} className="text-slate-400 hover:text-slate-900"><SafeIcon icon="⬅️" /></button>
        <div>
          <h1 className="text-xl font-bold">Booking / Demoer</h1>
          <p className="text-xs text-slate-500">Håndter innkommende demo-forespørsler</p>
        </div>
      </div>
      <div className="max-w-6xl mx-auto mt-8 px-6">
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-4 font-bold text-slate-600">Dato & Tid</th>
                <th className="px-6 py-4 font-bold text-slate-600">Bedrift / Navn</th>
                <th className="px-6 py-4 font-bold text-slate-600">Kontaktinfo</th>
                <th className="px-6 py-4 font-bold text-slate-600">Status</th>
                <th className="px-6 py-4 font-bold text-slate-600">Tildel Selger</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(demoBookings || []).map(booking => (
                <tr key={booking.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4">
                    <p className="font-bold text-slate-900">{booking.date}</p>
                    <p className="text-xs text-slate-500">Kl. {booking.time}</p>
                  </td>
                  <td className="px-6 py-4">
                    <p className="font-bold text-slate-900">{booking.company}</p>
                    <p className="text-xs text-slate-500">{booking.contact}</p>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm">{booking.email}</p>
                    <p className="text-xs text-slate-500">{booking.phone}</p>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded text-[10px] font-black uppercase tracking-widest ${booking.status === 'Ny' ? 'bg-blue-100 text-blue-800' : 'bg-emerald-100 text-emerald-800'}`}>
                      {booking.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {booking.status === 'Ny' ? (
                      <select onChange={(e) => updateDemoBooking(booking.id, { status: 'Tildelt', assignedTo: e.target.value })} className="p-2 border border-slate-300 rounded-lg text-xs outline-none focus:border-blue-500">
                        <option value="">Velg selger...</option>
                        {(users || []).map(u => <option key={u.user_id} value={u.name}>{u.name}</option>)}
                      </select>
                    ) : (
                      <span className="font-bold text-slate-700">{booking.assignedTo}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderResources = () => (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
      <div className="bg-white px-8 py-6 border-b border-slate-200 flex items-center gap-4">
        <button onClick={() => setActiveView('grid')} className="text-slate-400 hover:text-slate-900"><SafeIcon icon="⬅️" /></button>
        <div>
          <h1 className="text-xl font-bold">Portal Innhold & Verktøy</h1>
          <p className="text-xs text-slate-500">Last opp innhold til Selger- og Partnerportalene</p>
        </div>
      </div>
      
      <div className="max-w-6xl mx-auto mt-8 px-6 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1">
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
            <h3 className="font-bold text-slate-900 mb-4">Legg til nytt innhold</h3>
            <form onSubmit={(e) => { e.preventDefault(); addPortalResource(newResource); setNewResource({ title: '', type: 'Document', audience: 'all', description: '' }); }} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Tittel</label>
                <input required value={newResource.title} onChange={e=>setNewResource({...newResource, title: e.target.value})} className="w-full p-2 border rounded-lg text-sm" placeholder="F.eks. Produktark 2024" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Beskrivelse</label>
                <textarea required rows="2" value={newResource.description} onChange={e=>setNewResource({...newResource, description: e.target.value})} className="w-full p-2 border rounded-lg text-sm resize-none" placeholder="Kort beskrivelse..." />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Type</label>
                  <select value={newResource.type} onChange={e=>setNewResource({...newResource, type: e.target.value})} className="w-full p-2 border rounded-lg text-sm bg-white">
                    <option value="PDF">PDF</option>
                    <option value="Document">Dokument</option>
                    <option value="Video">Video</option>
                    <option value="Link">Ekstern Link</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Synlig for</label>
                  <select value={newResource.audience} onChange={e=>setNewResource({...newResource, audience: e.target.value})} className="w-full p-2 border rounded-lg text-sm bg-white">
                    <option value="all">Alle (Selger+Partner)</option>
                    <option value="seller">Kun Selgere</option>
                    <option value="partner">Kun Partnere</option>
                  </select>
                </div>
              </div>
              <button type="submit" className="w-full py-3 bg-blue-600 text-white rounded-xl font-bold text-sm hover:bg-blue-700 transition-colors">
                Publiser
              </button>
            </form>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-4">
          {(portalResources || []).map(res => (
            <div key={res.id} className="bg-white p-4 rounded-xl border border-slate-200 flex justify-between items-center shadow-sm">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl ${res.type === 'PDF' ? 'bg-red-50 text-red-600' : res.type === 'Video' ? 'bg-purple-50 text-purple-600' : 'bg-blue-50 text-blue-600'}`}>
                  <SafeIcon icon={res.type === 'PDF' ? "📄" : res.type === 'Video' ? "🎥" : "📝"} />
                </div>
                <div>
                  <h4 className="font-bold text-slate-900">{res.title}</h4>
                  <p className="text-xs text-slate-500">{res.description}</p>
                  <div className="flex gap-2 mt-2">
                    <span className="text-[9px] font-black uppercase tracking-widest bg-slate-100 px-2 py-0.5 rounded text-slate-500">{res.type}</span>
                    <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded ${res.audience === 'all' ? 'bg-emerald-50 text-emerald-600' : res.audience === 'seller' ? 'bg-blue-50 text-blue-600' : 'bg-indigo-50 text-indigo-600'}`}>
                      Målgruppe: {res.audience}
                    </span>
                  </div>
                </div>
              </div>
              <button onClick={() => deletePortalResource(res.id)} className="p-2 hover:bg-red-50 text-slate-300 hover:text-red-500 rounded-lg transition-colors">
                <SafeIcon icon="🗑️" />
              </button>
            </div>
          ))}
          {(!portalResources || portalResources.length === 0) && (
            <div className="text-center py-12 bg-white rounded-xl border-2 border-dashed border-slate-200">
              <p className="text-slate-500">Ingen ressurser lagt til enda.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderSectionHeader = (title, description) => (
    <div className="bg-white px-8 py-6 border-b border-slate-200 flex items-center gap-4">
      <button onClick={() => setActiveView('grid')} className="text-slate-400 hover:text-slate-900"><SafeIcon icon="⬅️" /></button>
      <div>
        <h1 className="text-xl font-bold">{title}</h1>
        <p className="text-xs text-slate-500">{description}</p>
      </div>
    </div>
  );

  const renderCountries = () => (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
      {renderSectionHeader('Land & avdelinger', 'Administrer plattformens markeder og avdelinger')}
      <div className="max-w-6xl mx-auto mt-8 px-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <form
          onSubmit={event => {
            event.preventDefault();
            const name = countryForm.name.trim();
            const code = countryForm.code.trim().toUpperCase();
            if (!name || !code) return;
            setCountries(current => [...current, { id: code.toLowerCase(), name, code, departments: [] }]);
            setCountryForm({ name: '', code: '' });
          }}
          className="bg-white rounded-2xl border border-slate-200 p-6"
        >
          <h2 className="font-bold">Legg til land</h2>
          <input
            required
            value={countryForm.name}
            onChange={event => setCountryForm({ ...countryForm, name: event.target.value })}
            placeholder="Land"
            className="w-full mt-4 p-3 border rounded-xl text-sm"
          />
          <input
            required
            maxLength="2"
            value={countryForm.code}
            onChange={event => setCountryForm({ ...countryForm, code: event.target.value })}
            placeholder="Kode, f.eks. FI"
            className="w-full mt-3 p-3 border rounded-xl text-sm uppercase"
          />
          <button className="w-full mt-4 py-3 rounded-xl bg-slate-900 text-white text-sm font-bold">
            Legg til land
          </button>
        </form>

        <form
          onSubmit={event => {
            event.preventDefault();
            const name = departmentForm.name.trim();
            if (!name) return;
            setCountries(current => current.map(country =>
              country.id === departmentForm.countryId && !country.departments.includes(name)
                ? { ...country, departments: [...country.departments, name] }
                : country
            ));
            setDepartmentForm({ ...departmentForm, name: '' });
          }}
          className="bg-white rounded-2xl border border-slate-200 p-6"
        >
          <h2 className="font-bold">Legg til avdeling</h2>
          <select
            value={departmentForm.countryId}
            onChange={event => setDepartmentForm({ ...departmentForm, countryId: event.target.value })}
            className="w-full mt-4 p-3 border rounded-xl text-sm bg-white"
          >
            {countries.map(country => <option key={country.id} value={country.id}>{country.name}</option>)}
          </select>
          <input
            required
            value={departmentForm.name}
            onChange={event => setDepartmentForm({ ...departmentForm, name: event.target.value })}
            placeholder="Avdelingsnavn"
            className="w-full mt-3 p-3 border rounded-xl text-sm"
          />
          <button className="w-full mt-4 py-3 rounded-xl bg-blue-600 text-white text-sm font-bold">
            Legg til avdeling
          </button>
        </form>

        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="font-bold mb-4">Registrerte land</h2>
          <div className="space-y-3">
            {countries.map(country => (
              <div key={country.id} className="p-4 rounded-xl bg-slate-50">
                <div className="flex justify-between font-bold">
                  <span>{country.name}</span>
                  <span className="text-xs text-slate-400">{country.code}</span>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {country.departments.map(item => (
                    <span key={item} className="px-2 py-1 rounded bg-white text-xs text-slate-600">{item}</span>
                  ))}
                  {country.departments.length === 0 && <span className="text-xs text-slate-400">Ingen avdelinger</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderPartners = () => (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
      {renderSectionHeader('Partnere', 'Administrer partnerstatus og partnerkunder')}
      <div className="max-w-6xl mx-auto mt-8 px-6 space-y-6">
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="font-bold">Aktive partnere</h2>
          </div>
          <div className="divide-y divide-slate-100">
            {(partnerList || []).map(partner => (
              <div key={partner.id} className="px-6 py-4 flex justify-between">
                <span className="font-bold">{partner.name}</span>
                <span className="text-sm text-slate-500">
                  {Number(partner.commissionRate || 0) * 100}% provisjon · {(partner.customers || []).length} kunder
                </span>
              </div>
            ))}
            {partnerList.length === 0 && <div className="p-6 text-sm text-slate-500">Ingen aktive partnere.</div>}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="font-bold">Ventende partnerforespørsler</h2>
          </div>
          <div className="divide-y divide-slate-100">
            {(pendingPartnerList || []).map(partner => (
              <div key={partner.id} className="px-6 py-4 flex items-center justify-between">
                <span className="font-bold">{partner.name || 'Ukjent partner'}</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setPendingPartnerList(current => current.filter(item => item.id !== partner.id));
                      setPartnerList(current => [...current, { ...partner, status: 'Active' }]);
                    }}
                    className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-xs font-bold"
                  >
                    Godkjenn
                  </button>
                  <button
                    onClick={() => setPendingPartnerList(current => current.filter(item => item.id !== partner.id))}
                    className="px-3 py-2 rounded-lg bg-red-50 text-red-700 text-xs font-bold"
                  >
                    Avvis
                  </button>
                </div>
              </div>
            ))}
            {pendingPartnerList.length === 0 && <div className="p-6 text-sm text-slate-500">Ingen ventende forespørsler.</div>}
          </div>
        </div>
      </div>
    </div>
  );

  const renderReports = () => {
    const totalMrr = (platformCustomers || []).reduce((sum, customer) => sum + Number(customer.monthly_value || 0), 0);
    const exportReport = () => {
      const report = {
        generated_at: new Date().toISOString(),
        customers: platformCustomers || [],
        profiles: platformProfiles || [],
        audit_log: platformAuditLog || []
      };
      const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `saleswork-systemrapport-${new Date().toISOString().slice(0, 10)}.json`;
      link.click();
      URL.revokeObjectURL(url);
    };

    return (
      <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
        {renderSectionHeader('Rapporter (system)', 'Oversikt over plattformens kunder, brukere og audit-hendelser')}
        <div className="max-w-7xl mx-auto mt-8 px-6">
          <div className="flex justify-end mb-5">
            <button onClick={exportReport} className="px-4 py-2 rounded-lg bg-slate-900 text-white text-xs font-bold">
              Eksporter rapport
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {[
              ['Kunder', (platformCustomers || []).length],
              ['Aktive kunder', activeCustomers.length],
              ['Plattformbrukere', (platformProfiles || []).length],
              ['Månedlig verdi', `${totalMrr.toLocaleString('nb-NO')} kr`]
            ].map(([label, value]) => (
              <div key={label} className="bg-white rounded-2xl border border-slate-200 p-5">
                <p className="text-xs uppercase tracking-widest text-slate-400 font-black">{label}</p>
                <p className="text-2xl font-black mt-2">{value}</p>
              </div>
            ))}
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200">
              <h2 className="font-bold">Siste administrative hendelser</h2>
            </div>
            {(platformAuditLog || []).slice(0, 25).map(event => (
              <div key={event.id} className="px-6 py-4 border-b border-slate-100 flex justify-between">
                <div>
                  <p className="font-bold text-sm">{event.action || 'Ukjent handling'}</p>
                  <p className="text-xs text-slate-500">{event.entity_type || 'System'}</p>
                </div>
                <span className="text-xs text-slate-500">
                  {event.created_at ? new Date(event.created_at).toLocaleString('nb-NO') : '—'}
                </span>
              </div>
            ))}
            {(platformAuditLog || []).length === 0 && <div className="p-8 text-sm text-slate-500">Ingen audit-hendelser registrert.</div>}
          </div>
        </div>
      </div>
    );
  };

  const renderGrid = () => (
    <div className="min-h-screen bg-[#0f172a] text-white p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-10 border-b border-slate-800 pb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center"><SafeIcon icon="🛡️" className="w-6 h-6" /></div>
            <div>
              <h1 className="text-2xl font-bold">SuperAdmin</h1>
              <p className="text-xs text-slate-400 mt-1">Innlogget som {currentUser?.email}</p>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {[
            { id: 'avdelinger', label: 'LAND & AVDELINGER', icon: '🌐', onClick: () => setActiveView('avdelinger') },
            { id: 'bedrifter', label: 'BEDRIFTER', icon: '🏢', badge: (platformCustomers || []).length, onClick: () => setActiveView('customers') },
            { id: 'kunder', label: 'KUNDER (BRUKERE)', icon: '👥', badge: (platformCustomers || []).length, onClick: () => setActiveView('customers') },
            { id: 'plattformkunder', title: 'Plattformkunder / Kundemiljøer', description: 'Opprett plattformkunder, administrer kundemiljøer og legg til brukere', icon: '🏢', onClick: () => navigate('/super-admin/tenants') },
            { id: 'ansatte', label: 'INTERNE ANSATTE', icon: '🛡️', badge: internalEmployees.length, onClick: () => setActiveView('employees') },
            { id: 'booking', label: 'BOOKING / DEMOER', icon: '📅', badge: (demoBookings || []).filter(d => d.status === 'Ny').length, onClick: () => setActiveView('booking') },
            { id: 'rabatter', label: 'PRISER OG RABATTER', icon: '🏷️', onClick: () => setActiveView('rabatter') },
            { id: 'partnere', label: 'PARTNERE', icon: '🤝', badge: (pendingPartners || []).length, onClick: () => setActiveView('partnere') },
            { id: 'ressurser', label: 'PORTAL INNHOLD', icon: '📚', onClick: () => setActiveView('ressurser') },
            { id: 'rapporter', label: 'RAPPORTER (SYSTEM)', icon: '📊', badge: (platformAuditLog || []).length, onClick: () => setActiveView('rapporter') }
          ].map(item => (
            <button key={item.id} onClick={item.onClick} className="bg-[#1e293b] border border-[#334155] rounded-xl p-6 flex flex-col items-center justify-center gap-4 hover:bg-[#334155] transition-colors relative h-32">
              {item.badge > 0 && <span className="absolute top-2 right-2 w-5 h-5 bg-red-500 rounded-full text-[10px] flex items-center justify-center font-bold">{item.badge}</span>}
              <SafeIcon icon={item.icon} className="text-3xl text-emerald-400" />
              {item.title ? (
                <span className="text-[9px] font-bold text-slate-300 text-center leading-tight">
                  <span className="block">{item.title}</span>
                  <span className="block mt-1 text-[8px] font-normal text-slate-400 normal-case tracking-normal">{item.description}</span>
                </span>
              ) : (
                <span className="text-[9px] font-bold text-slate-300 text-center uppercase tracking-widest leading-tight">{item.label}</span>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  if (!currentUser?.isSuperAdmin) return null;
  if (activeView === 'customers') return renderCustomers();
  if (activeView === 'employees') return renderEmployees();
  if (activeView === 'booking') return renderBookings();
  if (activeView === 'ressurser') return renderResources();
  if (activeView === 'avdelinger') return renderCountries();
  if (activeView === 'rabatter') {
    return (
      <PricingDiscountsView
        onBack={() => setActiveView('grid')}
        plans={plans}
        discountCodes={discountCodes}
        discountCodesLoading={discountCodesLoading}
        loadDiscountCodes={loadDiscountCodes}
        createDiscountCode={createDiscountCode}
        updateDiscountCode={updateDiscountCode}
      />
    );
  }
  if (activeView === 'partnere') return renderPartners();
  if (activeView === 'rapporter') return renderReports();
  
  return renderGrid();
};

export default SuperAdmin;